import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Input, Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getClientImages } from '../../slices/veloxity/pushNotification/getClientImages';
import { uploadImage } from '../../slices/veloxity/pushNotification/uploadImage';
import { FaPlus } from 'react-icons/fa';

const ImageSelectorModal = ({ isOpen, toggle, onSelectImage }) => {
    const dispatch = useDispatch();
    const [selectedImage, setSelectedImage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const fileInputRef = React.createRef();

    // Redux state
    const { images = [], loading: imagesLoading } = useSelector((state) => state.getClientImages || {});
    const { loading: uploadLoading } = useSelector((state) => state.uploadImage || {});

    useEffect(() => {
        if (isOpen) {
            dispatch(getClientImages());
        }
    }, [dispatch, isOpen]);

    const openFileDialog = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!['image/jpeg', 'image/png'].includes(file.type)) {
                setUploadError('Only JPEG and PNG images are allowed');
                return;
            }
            
            if (file.size > 102400) {
                setUploadError('Image size must be less than 100KB');
                return;
            }
            
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);
            
            img.onload = function() {
                URL.revokeObjectURL(objectUrl);
                
                if (img.width !== 360 || img.height !== 480) {
                    setUploadError(`Image dimensions must be 360x480 pixels. Current dimensions: ${img.width}x${img.height}`);
                    return;
                }
                
                handleUpload(file);
            };
            
            img.src = objectUrl;
        }
    };

    const handleUpload = (file) => {
        setUploadError(null);
        setUploadSuccess(false);
        setUploading(true);

        dispatch(uploadImage(file))
            .then((response) => {
                if (response.payload) {
                    setUploading(false);
                    setUploadSuccess(true);
                    dispatch(getClientImages());
                } else {
                    setUploading(false);
                    setUploadError("Upload failed: " + (response.error?.message || "Unknown error"));
                }
            })
            .catch(error => {
                setUploading(false);
                setUploadError("Upload failed: " + (error.message || "Unknown error"));
            });
    };

    const handleImageSelect = (image) => {
        setSelectedImage(image);
    };

    const handleConfirm = () => {
        if (selectedImage) {
            const imageWithUrl = {
                ...selectedImage,
                url: selectedImage.imageUrl
            };
            onSelectImage(imageWithUrl);
            toggle();
        } else {
            alert("Please select an image first.");
        }
    };

    const processImages = () => {
        if (!images) return [];
        
        if (!Array.isArray(images) && images.data) {
            return Array.isArray(images.data) ? images.data : [];
        }
        
        return Array.isArray(images) ? images : [];
    };

    const processedImages = processImages();

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>Select Image</ModalHeader>
            <ModalBody className="p-2">
                {uploadError && (
                    <Alert color="danger" className="mb-2">
                        {uploadError}
                    </Alert>
                )}
                
                {uploadSuccess && (
                    <Alert color="success" className="mb-2">
                        Image uploaded successfully!
                    </Alert>
                )}
                
                <div className="alert alert-info mb-3">
                    <p className="mb-1">• Image resolution should be 360x480 px</p>
                    <p className="mb-1">• Only jpeg and png images will be accepted</p>
                    <p className="mb-0">• Maximum size is 100 Kbytes</p>
                </div>

                <Input
                    type="file" 
                    id="newImage"
                    name="newImage"
                    accept="image/jpeg,image/png"
                    onChange={handleFileChange} 
                    style={{ display: 'none' }}
                    innerRef={fileInputRef}
                />

                {imagesLoading ? (
                    <div className="text-center p-4">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <div className="image-grid">
                        <div className="image-grid-item">
                            <div 
                                className="upload-item d-flex flex-column align-items-center justify-content-center"
                                onClick={openFileDialog}
                            >
                                <FaPlus size={30} className="mb-2" />
                                <div className="text-center">Upload New</div>
                                {uploading && (
                                    <Spinner size="sm" color="primary" className="mt-2" />
                                )}
                            </div>
                        </div>

                        {processedImages.map((image, index) => (
                            <div key={index} className="image-grid-item">
                                <div 
                                    className={`image-item ${selectedImage === image ? 'selected' : ''}`}
                                    onClick={() => handleImageSelect(image)}
                                >
                                    <img 
                                        src={image.imageUrl} 
                                        alt={`Image ${index + 1}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                <Button 
                    color="primary" 
                    onClick={handleConfirm} 
                    disabled={!selectedImage || uploadLoading}
                    style={{ backgroundColor: '#CC1977', borderColor: '#CC1977' }}
                >
                    Select Image
                </Button>
            </ModalFooter>

            <style jsx>{`
                .image-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 8px;
                    max-height: 450px;
                    overflow-y: auto;
                    padding: 8px;
                }
                
                .image-grid-item {
                    aspect-ratio: 1 / 1;
                    position: relative;
                }
                
                .image-item, .upload-item {
                    cursor: pointer;
                    border-radius: 4px;
                    overflow: hidden;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }
                
                .image-item {
                    border: 1px solid #ddd;
                    background-color: #fff;
                }
                
                .image-item:hover {
                    border-color: #aaa;
                }
                
                .image-item.selected {
                    border: 2px solid #CC1977;
                    box-shadow: 0 0 0 1px #CC1977;
                }
                
                .image-item img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
                
                .upload-item {
                    border: 1px dashed #ccc;
                    background-color: #f9f9f9;
                    padding: 10px;
                }
                
                .upload-item:hover {
                    background-color: #f0f0f0;
                    border-color: #aaa;
                }
                
                @media (max-width: 768px) {
                    .image-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
            `}</style>
        </Modal>
    );
};

export default ImageSelectorModal; 